import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp, TrendingDown, Users, User, ChevronDown, Copy, Check,
  CreditCard, PiggyBank, CalendarClock, Sparkles, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Search, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { useOracle } from "@/contexts/OracleContext";
import { TroquinhoCalendar } from "@/components/shared/TroquinhoCalendar";
import { subscribeToTransactionsRange } from "@/services/transactionsService";
import { subscribeToConnections } from "@/services/connectionsService";
import { subscribeToGoals } from "@/services/goalsService";

// ==================== CONSTANTES ====================

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];
const MONTHS_SHORT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// Mesmas cores da tela de Relatórios, em hexadecimal (os gráficos não aceitam classes do Tailwind)
const CATEGORY_HEX = {
  "Salário": "#10b981", "Freelance": "#14b8a6", "Investimentos": "#06b6d4",
  "Presentes": "#ec4899", "Outros": "#64748b", "Alimentação": "#f97316",
  "Moradia": "#3b82f6", "Transporte": "#f59e0b", "Saúde": "#ef4444",
  "Educação": "#6366f1", "Lazer": "#a855f7", "Vestuário": "#f43f5e",
  "Mercado": "#84cc16", "Delivery": "#fb923c", "Assinaturas": "#8b5cf6",
  "Contas": "#6b7280",
};
const GENERIC_HEX = ["#0ea5e9", "#8b5cf6", "#10b981", "#f43f5e", "#f59e0b", "#d946ef"];

// ==================== HELPERS ====================

const formatCurrency = (value) =>
  (Number(value) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getCategoryHex = (category) => {
  if (!category || typeof category !== "string") return "#94a3b8";
  if (CATEGORY_HEX[category]) return CATEGORY_HEX[category];
  const index = category.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return GENERIC_HEX[index % GENERIC_HEX.length];
};

const monthKeyOf = (year, month) => `${year}-${String(month + 1).padStart(2, "0")}`;
const txMonthKey = (t) => (t.dueDate || t.date || "").slice(0, 7);
const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const isCredit = (t) => ["credit", "credito"].includes(t.paymentMethod);

// Mesma regra do Contas & Consequências
const isInstallment = (t) =>
  isCredit(t) &&
  !!t.seriesId &&
  (t.seriesTotalMonths || 0) > 1 &&
  (t.seriesKind ?? "installment") === "installment";

// Mesma regra de valores da tela de Relatórios
const getValue = (t, participant) => {
  if (participant === "all") return t.amount || 0;
  if (participant === "me") return t.myShare ?? t.amount ?? 0;
  return t.participants?.find((p) => p.id === participant)?.share ?? 0;
};

const pctLabel = (pct) => `${Math.round(Math.abs(pct))}%`;

// ==================== COMPONENTE ====================

export default function PenteFino() {
  const { user } = useAuth();
  const { isOracleActive } = useOracle();

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedParticipant, setSelectedParticipant] = useState("all");
  const [selectedCardId, setSelectedCardId] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [connections, setConnections] = useState({ people: [], cards: [], banks: [], categories: [] });
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Sincroniza o calendário com o Modo Oráculo
  useEffect(() => {
    const hoje = new Date();
    if (isOracleActive) {
      const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
      setCurrentMonth(dataFutura.getMonth());
      setCurrentYear(dataFutura.getFullYear());
    } else {
      setCurrentMonth(hoje.getMonth());
      setCurrentYear(hoje.getFullYear());
    }
  }, [isOracleActive]);

  // Janela de busca: 5 meses para trás e 6 para frente do mês selecionado
  const range = useMemo(() => {
    const start = new Date(currentYear, currentMonth - 5, 1);
    const end = new Date(currentYear, currentMonth + 7, 0);
    return { startDate: toISODate(start), endDate: toISODate(end) };
  }, [currentMonth, currentYear]);

  // Transações da janela
  useEffect(() => {
    if (!user?.uid) return;
    setIsLoading(true);
    const unsubscribe = subscribeToTransactionsRange(
      user.uid,
      range.startDate,
      range.endDate,
      (data) => {
        setTransactions(data);
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
    return () => unsubscribe();
  }, [user?.uid, range.startDate, range.endDate]);

  // Conexões e metas
  useEffect(() => {
    if (!user?.uid) return;
    const unsubscribeConnections = subscribeToConnections(user.uid, (data) => setConnections(data));
    const unsubscribeGoals = subscribeToGoals(user.uid, (data) => setGoals(data || []));
    return () => {
      unsubscribeConnections();
      unsubscribeGoals();
    };
  }, [user?.uid]);

  // Lista de meses da janela (offset 0 = mês selecionado)
  const months = useMemo(() => {
    const list = [];
    for (let offset = -5; offset <= 6; offset++) {
      const d = new Date(currentYear, currentMonth + offset, 1);
      list.push({ offset, key: monthKeyOf(d.getFullYear(), d.getMonth()), label: MONTHS_SHORT[d.getMonth()] });
    }
    return list;
  }, [currentMonth, currentYear]);

  const currentKey = monthKeyOf(currentYear, currentMonth);
  const prevKey = months.find((m) => m.offset === -1)?.key;

  const monthTx = useMemo(() => transactions.filter((t) => txMonthKey(t) === currentKey), [transactions, currentKey]);
  const prevTx = useMemo(() => transactions.filter((t) => txMonthKey(t) === prevKey), [transactions, prevKey]);

  // Totais por mês (respeitando o filtro de pessoa)
  const byMonth = useMemo(() => {
    const map = {};
    months.forEach((m) => { map[m.key] = { income: 0, expense: 0 }; });
    transactions.forEach((t) => {
      const key = txMonthKey(t);
      if (!map[key]) return;
      const value = getValue(t, selectedParticipant);
      if (t.type === "income") map[key].income += value;
      else if (t.type === "expense") map[key].expense += value;
    });
    return map;
  }, [transactions, months, selectedParticipant]);

  // ===== Indicadores rápidos =====
  const kpis = useMemo(() => {
    const now = byMonth[currentKey] || { income: 0, expense: 0 };

    // Média dos 3 meses anteriores (ignora meses sem nenhum lançamento)
    const past = months
      .filter((m) => m.offset >= -3 && m.offset <= -1)
      .map((m) => byMonth[m.key])
      .filter((v) => v && v.income + v.expense > 0);
    const avg = past.length ? past.reduce((s, v) => s + v.expense, 0) / past.length : null;
    const vsAvg = avg ? ((now.expense - avg) / avg) * 100 : null;

    // Média por dia
    const today = new Date();
    const isCurrentMonth = today.getMonth() === currentMonth && today.getFullYear() === currentYear;
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const days = isCurrentMonth ? today.getDate() : daysInMonth;

    // Guardado no mês (movimentações das Metas)
    let saved = 0;
    goals.forEach((g) => {
      (g.movements || []).forEach((m) => {
        if ((m.date || "").slice(0, 7) !== currentKey) return;
        if (selectedParticipant !== "all" && m.participantId !== selectedParticipant) return;
        saved += m.type === "deposit" ? (m.amount || 0) : -(m.amount || 0);
      });
    });
    const savingsRate = now.income > 0 ? (saved / now.income) * 100 : null;

    return { ...now, avg, vsAvg, dailyAvg: now.expense / Math.max(1, days), saved, savingsRate };
  }, [byMonth, months, currentKey, currentMonth, currentYear, goals, selectedParticipant]);

  // ===== Gráfico dos últimos 6 meses =====
  const chartData = useMemo(
    () =>
      months
        .filter((m) => m.offset <= 0)
        .map((m) => ({
          label: m.label,
          Entradas: Math.round((byMonth[m.key]?.income || 0) * 100) / 100,
          Saídas: Math.round((byMonth[m.key]?.expense || 0) * 100) / 100,
        })),
    [months, byMonth]
  );
  const hasChartData = chartData.some((d) => d.Entradas > 0 || d.Saídas > 0);

  // ===== Categorias em alta e em baixa =====
  const categoryChanges = useMemo(() => {
    const sumByCategory = (list) => {
      const map = new Map();
      list
        .filter((t) => t.type === "expense")
        .forEach((t) => {
          const category = t.category || "Outros";
          map.set(category, (map.get(category) || 0) + getValue(t, selectedParticipant));
        });
      return map;
    };
    const now = sumByCategory(monthTx);
    const prev = sumByCategory(prevTx);
    const names = new Set([...now.keys(), ...prev.keys()]);

    return [...names]
      .map((name) => {
        const a = now.get(name) || 0;
        const b = prev.get(name) || 0;
        return { name, now: a, prev: b, diff: a - b, pct: b > 0 ? ((a - b) / b) * 100 : null };
      })
      .filter((c) => Math.abs(c.diff) >= 0.01)
      .sort((x, y) => Math.abs(y.diff) - Math.abs(x.diff))
      .slice(0, 6);
  }, [monthTx, prevTx, selectedParticipant]);

  // ===== Pente fino do cartão (sempre o valor total da fatura) =====
  const cardsWithData = useMemo(() => {
    const ids = new Set(
      monthTx.filter((t) => t.type === "expense" && isCredit(t) && t.cardId).map((t) => t.cardId)
    );
    return connections.cards.filter((c) => ids.has(c.id));
  }, [monthTx, connections.cards]);

  const activeCardId = cardsWithData.some((c) => c.id === selectedCardId)
    ? selectedCardId
    : cardsWithData[0]?.id || null;

  const cardAnalysis = useMemo(() => {
    if (!activeCardId) return null;
    const list = monthTx.filter((t) => t.type === "expense" && isCredit(t) && t.cardId === activeCardId);
    const total = list.reduce((s, t) => s + (t.amount || 0), 0);
    const committed = list
      .filter((t) => isInstallment(t) && t.seriesIndex > 1)
      .reduce((s, t) => s + (t.amount || 0), 0);

    const catMap = new Map();
    list.forEach((t) => {
      const category = t.category || "Outros";
      catMap.set(category, (catMap.get(category) || 0) + (t.amount || 0));
    });
    const categories = [...catMap.entries()]
      .map(([name, value]) => ({
        name,
        value: Math.round(value * 100) / 100,
        color: getCategoryHex(name),
        pct: total > 0 ? Math.round((value / total) * 100) : 0,
      }))
      .sort((a, b) => b.value - a.value);

    return {
      total,
      committed,
      committedPct: total > 0 ? Math.round((committed / total) * 100) : 0,
      categories,
      count: list.length,
    };
  }, [monthTx, activeCardId]);

  // ===== Compromisso futuro (parcelas já lançadas nos próximos 6 meses) =====
  const futureCommitments = useMemo(
    () =>
      months
        .filter((m) => m.offset >= 1)
        .map((m) => ({
          key: m.key,
          label: m.label,
          value: transactions
            .filter((t) => t.type === "expense" && isInstallment(t) && txMonthKey(t) === m.key)
            .reduce((s, t) => s + getValue(t, selectedParticipant), 0),
        })),
    [months, transactions, selectedParticipant]
  );
  const futureTotal = futureCommitments.reduce((s, m) => s + m.value, 0);
  const futureMax = Math.max(1, ...futureCommitments.map((m) => m.value));

  // ===== Placar da evolução (só no filtro "Todos") =====
  const scoreboard = useMemo(() => {
    if (selectedParticipant !== "all" || connections.people.length === 0) return null;

    const persons = [{ id: "me", name: "Eu" }, ...connections.people.map((p) => ({ id: p.id, name: p.name }))];
    const expensesNow = monthTx.filter((t) => t.type === "expense");
    const expensesPrev = prevTx.filter((t) => t.type === "expense");

    const rows = persons
      .map((p) => {
        const now = expensesNow.reduce((s, t) => s + getValue(t, p.id), 0);
        const prev = expensesPrev.reduce((s, t) => s + getValue(t, p.id), 0);

        const catMap = new Map();
        expensesNow.forEach((t) => {
          const v = getValue(t, p.id);
          if (v > 0) {
            const category = t.category || "Outros";
            catMap.set(category, (catMap.get(category) || 0) + v);
          }
        });
        const top = [...catMap.entries()].sort((a, b) => b[1] - a[1])[0];

        return {
          ...p,
          now,
          prev,
          pct: prev > 0 ? ((now - prev) / prev) * 100 : null,
          topCategory: top ? { name: top[0], pct: now > 0 ? Math.round((top[1] / now) * 100) : 0 } : null,
        };
      })
      .filter((r) => r.now > 0 || r.prev > 0);

    const comparable = rows.filter((r) => r.pct !== null);
    const best = comparable.length ? comparable.reduce((a, b) => (b.pct < a.pct ? b : a)) : null;
    const worst = comparable.length ? comparable.reduce((a, b) => (b.pct > a.pct ? b : a)) : null;

    return {
      rows,
      best: best && best.pct < 0 ? best : null,
      worst: worst && worst.pct > 0 ? worst : null,
    };
  }, [selectedParticipant, connections.people, monthTx, prevTx]);

  // ===== Frase do topo =====
  const headline = useMemo(() => {
    if (kpis.vsAvg === null) {
      return { tone: "neutral", text: "Ainda não há meses anteriores para comparar. Continue lançando e o pente fino fica mais afiado." };
    }
    if (kpis.vsAvg <= -10) {
      return { tone: "good", text: `${pctLabel(kpis.vsAvg)} abaixo da média dos últimos meses. Quem é você e o que fez com o antigo eu?` };
    }
    if (kpis.vsAvg < 10) {
      return { tone: "neutral", text: "Gastos dentro da média dos últimos meses. Estável, mas dá para apertar mais um furo do cinto." };
    }
    return { tone: "bad", text: `${pctLabel(kpis.vsAvg)} acima da média dos últimos meses. Hora de passar o pente fino de verdade.` };
  }, [kpis.vsAvg]);

  // ===== Copia e cola =====
  const participantName =
    selectedParticipant === "all"
      ? null
      : selectedParticipant === "me"
        ? "Eu"
        : connections.people.find((p) => p.id === selectedParticipant)?.name;

  const buildReport = () => {
    let r = `🔍 PENTE FINO – ${MONTHS[currentMonth]} ${currentYear}\n`;
    if (participantName) r += `(visão: ${participantName})\n`;

    r += `\n💸 Gastos: R$ ${formatCurrency(kpis.expense)}`;
    if (kpis.vsAvg !== null) {
      r += ` (${kpis.vsAvg <= 0 ? "▼" : "▲"} ${pctLabel(kpis.vsAvg)} vs média dos 3 meses)`;
    }
    r += `\n💰 Entradas: R$ ${formatCurrency(kpis.income)}`;
    if (kpis.saved !== 0) {
      r += `\n🐷 Guardado: R$ ${formatCurrency(kpis.saved)}`;
      if (kpis.savingsRate !== null) r += ` (${Math.round(kpis.savingsRate)}% das entradas)`;
    }

    if (scoreboard && (scoreboard.best || scoreboard.worst)) {
      r += `\n`;
      if (scoreboard.best) r += `\n🌟 Destaque: ${scoreboard.best.name} (−${pctLabel(scoreboard.best.pct)})`;
      if (scoreboard.worst) {
        r += `\n🚨 Gastão: ${scoreboard.worst.name} (+${pctLabel(scoreboard.worst.pct)}`;
        if (scoreboard.worst.topCategory) r += ` em ${scoreboard.worst.topCategory.name}`;
        r += `)`;
      }
    }

    const ups = categoryChanges.filter((c) => c.diff > 0).slice(0, 3);
    const downs = categoryChanges.filter((c) => c.diff < 0).slice(0, 3);
    if (ups.length || downs.length) {
      r += `\n\n📊 Categorias vs mês anterior`;
      ups.forEach((c) => {
        r += `\n📈 ${c.name}: ${c.pct !== null ? `+${pctLabel(c.pct)}` : "novo"} (R$ ${formatCurrency(c.now)})`;
      });
      downs.forEach((c) => {
        r += `\n📉 ${c.name}: −${pctLabel(c.pct ?? 100)} (R$ ${formatCurrency(c.now)})`;
      });
    }

    if (futureTotal > 0) {
      r += `\n\n💳 Já comprometido em parcelas nos próximos 6 meses: R$ ${formatCurrency(futureTotal)}`;
    }

    r += `\n\nFeito com 🐷 Troquinho`;
    return r;
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(buildReport());
      setCopied(true);
      toast.success("Pente fino copiado! Agora é só colar no grupo 😏");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Não foi possível copiar. Tente novamente.");
    }
  };

  const headlineStyles = {
    good: "bg-emerald-50 border-emerald-200 text-emerald-700",
    neutral: "bg-slate-100 border-slate-200 text-slate-600",
    bad: "bg-amber-50 border-amber-200 text-amber-700",
  };

  const tooltipFormatter = (value) => `R$ ${formatCurrency(value)}`;

  return (
    <div className={`min-h-screen pb-32 transition-colors duration-500 ${isOracleActive ? "bg-indigo-50/50" : "bg-slate-50"}`}>
      <div className="max-w-lg mx-auto px-5 py-6">

        {/* Header */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 text-center">
          <h1 className={`text-2xl font-black tracking-tight mb-1 ${isOracleActive ? "text-indigo-900" : "text-slate-900"}`}>
            Pente Fino
          </h1>
          <p className="text-slate-500 text-sm italic">Para onde o dinheiro está indo, e como trazer ele de volta.</p>
        </motion.header>

        {/* Calendário */}
        <div className="flex justify-center mb-8">
          <TroquinhoCalendar
            currentMonth={currentMonth}
            currentYear={currentYear}
            onPeriodChange={(month, year) => {
              setCurrentMonth(month);
              setCurrentYear(year);
            }}
          />
        </div>

        {/* Filtro de pessoas (igual Relatórios) */}
        <div className="flex flex-wrap gap-2 pb-4 justify-center mb-2">
          <Button
            variant={selectedParticipant === "all" ? "default" : "outline"}
            size="sm"
            className="rounded-full whitespace-nowrap"
            onClick={() => setSelectedParticipant("all")}
          >
            <Users className="w-4 h-4 mr-1" />
            Todos
          </Button>
          <Button
            variant={selectedParticipant === "me" ? "default" : "outline"}
            size="sm"
            className="rounded-full whitespace-nowrap"
            onClick={() => setSelectedParticipant("me")}
          >
            <User className="w-4 h-4 mr-1" />
            Eu
          </Button>
          {connections.people.length > 4 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full">
                  <Users className="w-4 h-4 mr-1" />
                  {participantName && selectedParticipant !== "me" ? participantName : "Pessoas"}
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {connections.people.map((person) => (
                  <DropdownMenuItem key={person.id} onClick={() => setSelectedParticipant(person.id)}>
                    {person.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            connections.people.map((person) => (
              <Button
                key={person.id}
                variant={selectedParticipant === person.id ? "default" : "outline"}
                size="sm"
                className="rounded-full whitespace-nowrap"
                onClick={() => setSelectedParticipant(person.id)}
              >
                {person.name}
              </Button>
            ))
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            <p className="text-slate-400 text-sm animate-pulse">Passando o pente fino...</p>
          </div>
        ) : (
          <div className="space-y-6">

            {/* Frase do mês */}
            <div className={`rounded-[20px] p-4 border text-sm flex items-start gap-2 ${headlineStyles[headline.tone]}`}>
              {headline.tone === "bad" ? (
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : (
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
              )}
              <span>{headline.text}</span>
            </div>

            {/* Indicadores rápidos */}
            <section className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                <p className="text-[11px] text-slate-400 font-bold mb-1">Gastos do mês</p>
                <p className="text-lg font-black text-rose-600 truncate">R$ {formatCurrency(kpis.expense)}</p>
                {kpis.vsAvg !== null && (
                  <p className={`text-[11px] font-bold flex items-center gap-0.5 mt-1 ${kpis.vsAvg <= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                    {kpis.vsAvg <= 0 ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                    {pctLabel(kpis.vsAvg)} vs média
                  </p>
                )}
              </div>

              <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
                <p className="text-[11px] text-slate-400 font-bold mb-1">Média por dia</p>
                <p className="text-lg font-black text-slate-800 truncate">R$ {formatCurrency(kpis.dailyAvg)}</p>
                {kpis.avg !== null && (
                  <p className="text-[11px] text-slate-400 font-medium mt-1 truncate">
                    Média mensal: R$ {formatCurrency(kpis.avg)}
                  </p>
                )}
              </div>

              <div className="col-span-2 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                    <PiggyBank className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-400 font-bold">Guardado nas Metas</p>
                    <p className="text-lg font-black text-slate-800">R$ {formatCurrency(kpis.saved)}</p>
                  </div>
                </div>
                {kpis.savingsRate !== null && (
                  <div className="text-right">
                    <p className="text-[11px] text-slate-400 font-bold">Das entradas</p>
                    <p className={`text-lg font-black ${kpis.savingsRate >= 10 ? "text-emerald-600" : "text-slate-500"}`}>
                      {Math.round(kpis.savingsRate)}%
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Últimos 6 meses */}
            <section>
              <h2 className="text-lg font-bold text-slate-900 mb-3">Últimos 6 meses</h2>
              <div className="bg-white rounded-[24px] p-4 shadow-sm border border-slate-100">
                {hasChartData ? (
                  <>
                    <div className="h-48 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} barGap={2} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                          <Tooltip
                            formatter={tooltipFormatter}
                            cursor={{ fill: "#f1f5f9" }}
                            contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                          />
                          <Bar dataKey="Entradas" fill="#10b981" radius={[6, 6, 0, 0]} />
                          <Bar dataKey="Saídas" fill="#f43f5e" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-4 mt-2 text-[11px] font-bold text-slate-500">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Entradas</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Saídas</span>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-slate-500 text-sm py-8">Sem lançamentos nos últimos meses para comparar.</p>
                )}
              </div>
            </section>

            {/* Categorias em alta e em baixa */}
            {categoryChanges.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-900 mb-1">Categorias vs mês anterior</h2>
                <p className="text-xs text-slate-500 mb-3">O que mais mudou de um mês para o outro.</p>
                <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100 space-y-3">
                  {categoryChanges.map((c) => {
                    const up = c.diff > 0;
                    return (
                      <div key={c.name} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: getCategoryHex(c.name) }} />
                          <span className="font-medium text-slate-700 truncate">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-sm text-slate-500">R$ {formatCurrency(c.now)}</span>
                          <span className={`text-xs font-black flex items-center gap-0.5 w-16 justify-end ${up ? "text-rose-500" : "text-emerald-600"}`}>
                            {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                            {c.pct !== null ? pctLabel(c.pct) : "novo"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Pente fino do cartão */}
            {cardsWithData.length > 0 && cardAnalysis && (
              <section>
                <h2 className="text-lg font-bold text-slate-900 mb-1">Pente fino do cartão</h2>
                <p className="text-xs text-slate-500 mb-3">Valor total da fatura, sem divisão.</p>

                {cardsWithData.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2 mb-2">
                    {cardsWithData.map((card) => (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => setSelectedCardId(card.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                          activeCardId === card.id
                            ? "bg-indigo-500 text-white shadow-md shadow-indigo-200"
                            : "bg-white text-slate-600 border border-slate-200"
                        }`}
                      >
                        <CreditCard className="w-4 h-4" />
                        {card.name}
                      </button>
                    ))}
                  </div>
                )}

                <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-32 flex-shrink-0 relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={cardAnalysis.categories}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={40}
                            outerRadius={60}
                            paddingAngle={2}
                            stroke="none"
                          >
                            {cardAnalysis.categories.map((c) => (
                              <Cell key={c.name} fill={c.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-[10px] text-slate-400 font-bold">Fatura</span>
                        <span className="text-xs font-black text-slate-800">R$ {formatCurrency(cardAnalysis.total)}</span>
                      </div>
                    </div>

                    <div className="flex-1 min-w-0 space-y-1.5">
                      {cardAnalysis.categories.slice(0, 5).map((c) => (
                        <div key={c.name} className="flex items-center justify-between gap-2 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                            <span className="text-slate-700 truncate">{c.name}</span>
                          </div>
                          <span className="text-slate-500 font-bold flex-shrink-0">{c.pct}%</span>
                        </div>
                      ))}
                      {cardAnalysis.categories.length > 5 && (
                        <p className="text-[11px] text-slate-400">+{cardAnalysis.categories.length - 5} categorias</p>
                      )}
                    </div>
                  </div>

                  {cardAnalysis.committed > 0 && (
                    <div className="mt-5 pt-4 border-t border-slate-100">
                      <div className="flex justify-between text-xs font-bold mb-1.5">
                        <span className="text-slate-600">Já vinha de parcelas antigas</span>
                        <span className="text-indigo-600">{cardAnalysis.committedPct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${cardAnalysis.committedPct}%` }} />
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">
                        R$ {formatCurrency(cardAnalysis.committed)} desta fatura já estavam comprometidos antes do mês começar.
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Compromisso futuro */}
            {futureTotal > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-indigo-500" />
                  Compromisso futuro
                </h2>
                <p className="text-xs text-slate-500 mb-3">Parcelas já lançadas para os próximos meses.</p>
                <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100 space-y-2.5">
                  {futureCommitments.map((m) => (
                    <div key={m.key} className="flex items-center gap-3">
                      <span className="w-8 text-xs font-bold text-slate-500">{m.label}</span>
                      <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-indigo-400 rounded-full"
                          style={{ width: `${Math.round((m.value / futureMax) * 100)}%` }}
                        />
                      </div>
                      <span className="w-24 text-right text-xs font-bold text-slate-700">R$ {formatCurrency(m.value)}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-1 border-t border-slate-100 flex justify-between text-sm">
                    <span className="text-slate-600 font-medium">Total comprometido</span>
                    <span className="font-black text-indigo-600">R$ {formatCurrency(futureTotal)}</span>
                  </div>
                </div>
              </section>
            )}

            {/* Placar da evolução */}
            {scoreboard && scoreboard.rows.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-900 mb-1">Placar da evolução</h2>
                <p className="text-xs text-slate-500 mb-3">Cada um comparado com ele mesmo no mês anterior.</p>

                {(scoreboard.best || scoreboard.worst) && (
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {scoreboard.best && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                        <p className="text-2xl mb-1">🌟</p>
                        <p className="text-[11px] font-bold text-emerald-700">Destaque do mês</p>
                        <p className="font-black text-emerald-800 truncate">{scoreboard.best.name}</p>
                        <p className="text-xs text-emerald-700">gastou {pctLabel(scoreboard.best.pct)} a menos</p>
                      </div>
                    )}
                    {scoreboard.worst && (
                      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
                        <p className="text-2xl mb-1">🚨</p>
                        <p className="text-[11px] font-bold text-rose-700">Gastão do mês</p>
                        <p className="font-black text-rose-800 truncate">{scoreboard.worst.name}</p>
                        <p className="text-xs text-rose-700">gastou {pctLabel(scoreboard.worst.pct)} a mais</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white rounded-[24px] p-5 shadow-sm border border-slate-100 space-y-4">
                  {scoreboard.rows.map((row) => (
                    <div key={row.id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-bold text-slate-800 truncate">{row.name}</p>
                        {row.topCategory && (
                          <p className="text-[11px] text-slate-500 truncate">
                            Ponto fraco: {row.topCategory.name} ({row.topCategory.pct}%)
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-black text-slate-800 text-sm">R$ {formatCurrency(row.now)}</p>
                        {row.pct !== null ? (
                          <p className={`text-[11px] font-bold flex items-center justify-end gap-0.5 ${row.pct <= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                            {row.pct <= 0 ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                            {pctLabel(row.pct)}
                          </p>
                        ) : (
                          <p className="text-[11px] text-slate-400">sem mês anterior</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Mês vazio */}
            {monthTx.length === 0 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <p className="font-bold text-slate-700 mb-1">Nada para pentear neste mês</p>
                <p className="text-slate-500 text-sm">Lance as despesas e receitas do mês para ver a análise completa.</p>
              </div>
            )}

            {/* Copia e cola */}
            <section>
              <Button
                variant="outline"
                className="w-full h-14 rounded-full font-semibold justify-start px-6"
                onClick={copyReport}
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center mr-4">
                  <Search className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-slate-900">Copiar o Pente Fino</p>
                  <p className="text-xs text-slate-500">Resumo para mandar no grupo</p>
                </div>
                {copied ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-slate-400" />}
              </Button>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
